type ScriptEntry = {
  promise: Promise<void>;
  refCount: number;
  loaded: boolean;
};

/**
 * SSR-safe script loader with URL-level cache + ref-count.
 * Infrastructure only — no business state.
 */
const scriptCache = new Map<string, ScriptEntry>();

export type ScriptLoaderOptions = {
  readonly src: string;
  readonly id?: string;
  readonly timeoutMs?: number;
  readonly attributes?: Readonly<Record<string, string>>;
};

export function loadScript(options: ScriptLoaderOptions): Promise<void> {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return Promise.reject(new Error('[ScriptLoader] Browser APIs unavailable'));
  }

  const existing = scriptCache.get(options.src);
  if (existing) {
    existing.refCount += 1;
    return existing.promise;
  }

  const timeoutMs = options.timeoutMs ?? 10_000;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = options.src;
    if (options.id) script.id = options.id;

    if (options.attributes) {
      for (const [key, value] of Object.entries(options.attributes)) {
        script.setAttribute(key, value);
      }
    }

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`[ScriptLoader] Timeout loading ${options.src}`));
    }, timeoutMs);

    const cleanup = (): void => {
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    };

    script.onload = () => {
      cleanup();
      const entry = scriptCache.get(options.src);
      if (entry) entry.loaded = true;
      resolve();
    };

    script.onerror = () => {
      cleanup();
      scriptCache.delete(options.src);
      reject(new Error(`[ScriptLoader] Failed to load ${options.src}`));
    };

    document.head.appendChild(script);
  });

  scriptCache.set(options.src, { promise, refCount: 1, loaded: false });
  return promise;
}

export function releaseScript(src: string): void {
  const entry = scriptCache.get(src);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  // Keep script in DOM for other instances; only drop cache entry when unused.
  if (entry.refCount === 0 && !entry.loaded) {
    scriptCache.delete(src);
  }
}

/** @internal tests */
export function _testClearScriptCache(): void {
  scriptCache.clear();
}
